import { Injectable, Signal, computed } from '@angular/core';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { IDatiqGeoSuggestion, IqProxyGeoService } from '@dat/iq-proxy';
import { gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { firstValueFrom } from 'rxjs';
import {
    ContractTypeEnum,
    ContributionEscalationLevelEnum,
    EquipmentTypeEnum,
    GraphQlContributions,
    IIqGraphRatesStateService,
    Quote,
    Rate,
    ContractType,
    EquipmentType,
    BrokerPaymentTypeEnum,
    Contribution,
    ShipperPaymentTypeEnum,
    UserTypeEnum,
    GraphQlContributionData,
    GraphQlHistorical,
    BestFitRate,
} from '../../models';
import { CUSTOMER_CONTRIBUTIONS_GRAPHQL_QUERY } from './queries';
import { getHistoricalRatesDataQuery } from './query-fragments';
import { isEmpty } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class IqGraphRatesStateService implements IIqGraphRatesStateService {
    constructor(
        private readonly _apollo: DatiqGraphqlApolloService,
        private readonly _geo: IqProxyGeoService,
    ) {}

    async getContributions(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
    ): Promise<Contribution[]> {
        const originSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(origin);
        const destinationSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(destination);
        if (!originSuggestion?.city || !destinationSuggestion?.city) {
            return null;
        }

        // Get contributions data from GraphQL
        const contributionsResponse: GraphQlContributions =
            await firstValueFrom(
                this._apollo.getApollo().query({
                    query: gql`
                        ${CUSTOMER_CONTRIBUTIONS_GRAPHQL_QUERY}
                    `,
                    variables: {
                        historicalQuery: {
                            origin: {
                                city: originSuggestion.city,
                                stateProv: originSuggestion.stateProv,
                            },
                            originEscalationLevel:
                                ContributionEscalationLevelEnum.MARKET_AREA,
                            destination: {
                                city: destinationSuggestion.city,
                                stateProv: destinationSuggestion.stateProv,
                            },
                            destinationEscalationLevel:
                                ContributionEscalationLevelEnum.MARKET_AREA,
                            months: 13,
                        },
                    },
                }),
            );

        const historicalData = await firstValueFrom(
            this._apollo.getApollo().query({
                query: this.HistoricalQuery,
                variables: {
                    historicalQuery: {
                        timeEscalation: 'SEVENDAYS',
                        equipmentCategory: equipmentType,
                        timeInterval: 'Monthly',
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                        },
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                        },
                        rateType: contractType,
                    },
                },
            }),
        );
        let mileage = (historicalData as any)?.data?.historical?.mileage;

        // Filter the contributions based on contractType
        const contributionsData =
            contributionsResponse?.data?.getCustomerContributions;
        const filteredContributionsData: GraphQlContributionData[] =
            contractType === ContractTypeEnum.Contract
                ? this.filterContractContributions(contributionsData)
                : contractType === ContractTypeEnum.Spot
                ? this.filterSpotContributions(contributionsData)
                : null;

        // Map the data
        let contributions: Contribution[];
        contributions = filteredContributionsData
            .filter((cont) => cont.equipType === equipmentType)
            .map((cont) => {
                return {
                    origin: originSuggestion,
                    destination: destinationSuggestion,
                    equipmentType: equipmentType,
                    date: cont.pickupDate,
                    userType: cont.contributorType,
                    rateType: contractType,
                    value:
                        contractType === ContractTypeEnum.Spot
                            ? cont.contributorType === UserTypeEnum.Broker
                                ? perMile
                                    ? cont.totalPaidByBroker / mileage
                                    : cont.totalPaidByBroker
                                : perMile
                                ? cont.totalPaidByShipper / mileage
                                : cont.totalPaidByShipper
                            : perMile
                            ? cont.totalPaidByShipper / mileage
                            : cont.totalPaidByShipper,
                    shipperName: cont.shipperName,
                };
            });
        return contributions;
    }

    get ForecastQuery(): DocumentNode {
        return gql`
            query ($forecastQuery: Ratecast_SearchCriteriaInput) {
                forecast: RatecastPublic(query: $forecastQuery) {
                    mileage
                    rates: forecasts {
                        trip: perTrip {
                            date: forecastDate
                            average: forecastUSD
                            range: mae {
                                high: highUSD
                                low: lowUSD
                            }
                        }
                        mile: perMile {
                            date: forecastDate
                            average: forecastUSD
                            range: mae {
                                high: highUSD
                                low: lowUSD
                            }
                        }
                    }
                    escalationOrigin: origin
                    escalationDestination: destination
                }
            }
        `;
    }

    get HistoricalQuery(): DocumentNode {
        return gql`
            query ($historicalQuery: HISTORICAL_SearchCriteriaInputHistorical) {
                historical: getHistorical(query: $historicalQuery) {
                    mileage
                    rates {
                        trip {
                            date
                            average
                            range {
                                low
                                high
                            }
                        }
                        mile {
                            date
                            average
                            range {
                                low
                                high
                            }
                        }
                    }
                    reports
                    companies
                    timeEscalation
                    timeInterval
                    area
                    areaDestination
                    escalationOrigin
                    escalationDestination
                }
            }
        `;
    }

    async getThirteenMonthForecastRates(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
        quote: Quote,
    ): Promise<Rate[]> {
        if (
            !origin?.length ||
            !destination?.length ||
            !equipmentType?.length ||
            !contractType?.length
        ) {
            return null;
        }
        const originSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(origin);
        const destinationSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(destination);

        const forecastData = await firstValueFrom(
            this._apollo.getApollo().query({
                query: this.ForecastQuery,
                variables: {
                    forecastQuery: {
                        equipmentCategory: equipmentType,
                        forecastPeriod: 'THIRTEENMONTHSUMMARY',
                        maximumEscalation: 'COUNTRY',
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                            postalCode: destinationSuggestion.postalCode,
                        },
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                            postalCode: originSuggestion.postalCode,
                        },
                        rateType: contractType,
                    },
                },
            }),
        );

        const forecastRates = (forecastData as any)?.data?.forecast?.rates;
        if (!forecastRates) {
            return null;
        }
        const dataValues = perMile ? forecastRates?.mile : forecastRates?.trip;
        const mileage = (forecastData as any)?.data?.forecast?.mileage ?? 0;
        const escalationOrigin = (forecastData as any)?.data?.forecast?.escalationOrigin;
        const escalationDestination = (forecastData as any)?.data?.forecast?.escalationDestination;
        const response = dataValues.map((rate) => {
            return {
                average: this.addFuel(
                    rate.average,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                high: this.addFuel(
                    rate.range.high,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                low: this.addFuel(
                    rate.range.low,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                date: rate.date,
                escalationOrigin,
                escalationDestination
            };
        });
        return response;
    }

    async getThirteenMonthHistoricalRates(
        origin: string,
        destination: string,
        equipmentType: EquipmentTypeEnum,
        contractType: ContractTypeEnum,
        perMile: boolean,
        quote: Quote,
    ): Promise<Rate[]> {
        if (
            !origin?.length ||
            !destination?.length ||
            !equipmentType?.length ||
            !contractType?.length
        ) {
            return null;
        }
        const originSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(origin);
        const destinationSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(destination);

        const historicalData = await firstValueFrom(
            this._apollo.getApollo().query({
                query: this.HistoricalQuery,
                variables: {
                    historicalQuery: {
                        timeEscalation: 'SEVENDAYS',
                        equipmentCategory: equipmentType,
                        timeInterval: 'Monthly',
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                            postalCode: destinationSuggestion.postalCode,
                        },
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                            postalCode: originSuggestion.postalCode,
                        },
                        rateType: contractType,
                    },
                },
            }),
        );

        const historicalRates = (historicalData as any)?.data?.historical
            ?.rates;
        const escalationOrigin = (historicalData as any)?.data?.historical?.escalationOrigin;
        const escalationDestination = (historicalData as any)?.data?.historical?.escalationDestination;
        const dataValues = perMile ? historicalRates.mile : historicalRates.trip;

        const mileage = (historicalData as any)?.data?.historical?.mileage ?? 0;
        const response = dataValues.map((rate) => {
            return {
                average: this.addFuel(
                    rate.average,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                high: this.addFuel(
                    rate.range.high,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                low: this.addFuel(
                    rate.range.low,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                date: rate.date,
                escalationOrigin,
                escalationDestination
            };
        });
        return response;
    }

    async getThirtyFiveDayForecastRates(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
        quote: Quote,
    ): Promise<Rate[]> {
        if (
            !origin?.length ||
            !destination?.length ||
            !equipmentType?.length ||
            !contractType?.length
        ) {
            return null;
        }
        const originSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(origin);
        const destinationSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(destination);
        const forecastData = await firstValueFrom(
            this._apollo.getApollo().query({
                query: this.ForecastQuery,
                variables: {
                    forecastQuery: {
                        equipmentCategory: equipmentType,
                        forecastPeriod: 'THIRTYFIVEDAYS',
                        maximumEscalation: 'COUNTRY',
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                            postalCode: destinationSuggestion.postalCode,
                        },
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                            postalCode: originSuggestion.postalCode,
                        },
                        rateType: contractType,
                    },
                },
            }),
        );

        const forecastRates = (forecastData as any)?.data?.forecast?.rates;
        const escalationOrigin = (forecastData as any)?.data?.forecast?.escalationOrigin;
        const escalationDestination = (forecastData as any)?.data?.forecast?.escalationDestination;

        if (!forecastRates) {
            return null;
        }
        const dataValues = perMile ? forecastRates?.mile : forecastRates?.trip;
        const mileage = (forecastData as any)?.data?.forecast?.mileage ?? 0;

        const response = dataValues.map((rate) => {
            return {
                average: this.addFuel(
                    rate.average,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                high: this.addFuel(
                    rate.range.high,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                low: this.addFuel(
                    rate.range.low,
                    quote.fuel,
                    perMile,
                    mileage,
                ),
                date: rate.date,
                escalationOrigin,
                escalationDestination
            };
        });

        return response;
    }

    async getBestFitRate(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
        quote: Quote,
    ): Promise<BestFitRate> {
        if (!origin?.length || !destination?.length) {
            return {
                average: 0,
                high: 0,
                low: 0,
                escalation: '',
            };
        }
        const originResult = await this._geo.fetchFirstResult(origin);
        const destinationResult = await this._geo.fetchFirstResult(destination);

        const graphqlResponse = await this.makeHistoricalQuery(
            equipmentType,
            contractType,
            originResult,
            destinationResult,
        );
        const data = graphqlResponse?.data as GraphQlHistorical;

        if (isEmpty(data)) {
            return {
                average: 0,
                high: 0,
                low: 0,
                escalation: '',
            };
        }

        const timeEscalation = data.rate.timeEscalation;

        const averageRate = perMile
            ? data?.rate?.average?.perMile?.value
            : data?.rate?.average?.perTrip?.value;

        const highRate = perMile
            ? data?.rate?.high?.perMile?.value
            : data?.rate?.high?.perTrip?.value;
        const lowRate = perMile
            ? data?.rate?.low?.perMile?.value
            : data?.rate?.low?.perTrip?.value;

        return {
            average: this.addFuel(
                averageRate,
                quote.fuel,
                perMile,
                data.rate.mileage,
            ),
            high: this.addFuel(
                highRate,
                quote.fuel,
                perMile,
                data.rate.mileage,
            ),
            low: this.addFuel(
                lowRate,
                quote.fuel,
                perMile,
                data.rate?.mileage ?? 0,
            ),
            escalation: timeEscalation,
        };
    }

    private makeHistoricalQuery(
        equipmentType: string,
        contractType: string,
        originSuggestion: IDatiqGeoSuggestion,
        destinationSuggestion: IDatiqGeoSuggestion,
    ) {
        return firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    ${getHistoricalRatesDataQuery}
                `,
                variables: {
                    historicalQuery: {
                        equipmentCategory: equipmentType,
                        timeEscalation: 'THREEDAYS',
                        timeInterval: 'CurrentDay',
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                            postalCode: originSuggestion.postalCode,
                        },
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                            postalCode: destinationSuggestion.postalCode,
                        },
                        rateType: contractType,
                    },
                },
            }),
        );
    }

    getSuggestion(location: string): Promise<IDatiqGeoSuggestion> {
        return this._geo.fetchFirstResult(location);
    }

    private addFuel(
        rate: number,
        fuel: number,
        perMile: boolean,
        mileage: number,
    ) {
        if (perMile) {
            return rate + fuel;
        } else {
            return rate + fuel * mileage;
        }
    }

    private filterContractContributions(
        contributionsData: GraphQlContributionData[],
    ): GraphQlContributionData[] {
        return contributionsData.filter(
            (cont: GraphQlContributionData) =>
                (cont.contributorType === UserTypeEnum.Broker &&
                    (cont.brokerPaymentType === BrokerPaymentTypeEnum.BCC ||
                        cont.shipperPaymentType ===
                            ShipperPaymentTypeEnum.SBS ||
                        cont.shipperPaymentType ===
                            ShipperPaymentTypeEnum.SBC)) ||
                (cont.contributorType === UserTypeEnum.Shipper &&
                    cont.shipperPaymentType === ShipperPaymentTypeEnum.SCC) ||
                ((cont.contributorType === UserTypeEnum.Carrier ||
                    cont.contributorType === UserTypeEnum.ContractCarrier) &&
                    cont.shipperPaymentType === ShipperPaymentTypeEnum.SCC),
        );
    }

    private filterSpotContributions(
        contributionsData: GraphQlContributionData[],
    ): GraphQlContributionData[] {
        return contributionsData.filter(
            (cont: GraphQlContributionData) =>
                (cont.contributorType === UserTypeEnum.Broker &&
                    cont.brokerPaymentType === BrokerPaymentTypeEnum.BCS) ||
                (cont.contributorType === UserTypeEnum.Shipper &&
                    (cont.shipperPaymentType === ShipperPaymentTypeEnum.SBS ||
                        cont.brokerPaymentType ===
                            BrokerPaymentTypeEnum.BCS)) ||
                (cont.contributorType === UserTypeEnum.Carrier &&
                    cont.brokerPaymentType === BrokerPaymentTypeEnum.BCS),
        );
    }
}
